import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from '@capacitor-community/safe-area';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, watchFcmToken } from './firebase';
import { isNativePush, watchNativePush } from './nativePush';
import { isInCall } from './utils/presence';
import { logPresence } from './utils/presenceLog';
import { subscribeToCycle } from './utils/cycle';
import ErrorBoundary from './components/ErrorBoundary';
import AppLayout from './components/AppLayout';
import GlobalCallListener from './components/GlobalCallListener';
import { needsOnboarding } from './utils/onboarding';
import { mustWriteWhatsApp } from './utils/intro';
import useBackButton from './hooks/useBackButton';
import { isAdminUser } from './constants';
import { readCodeFromLocation, setPendingJoinCode, getPendingJoinCode, clearPendingJoinCode } from './utils/teacher';
import { readPeerFromPath, setPendingPeer, getPendingPeer, clearPendingPeer } from './utils/invite';
import { LANG_STORAGE_KEY, setFeedbackLanguage } from './utils/feedbackLanguage';
import { resolveAppLanguage, deviceTimeZone } from './utils/appLanguage';
import Logo from './components/Logo';
import { Mic } from 'lucide-react';

// Import thunks are kept separate from React.lazy so the bottom-nav tabs can
// be preloaded on idle (see the preload effect in App) — without this, the
// first visit to each tab downloads its chunk on the spot and the page flashes.
const importHome = () => import('./pages/Home');
const importChats = () => import('./pages/Chats');
const importAIChat = () => import('./pages/AinurHub');
const importProfile = () => import('./pages/Profile');
const importRanking = () => import('./pages/Ranking');
const importPlan = () => import('./pages/Plan');
const TAB_PAGE_IMPORTS = [importHome, importPlan, importChats, importAIChat, importProfile];

const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const Home = React.lazy(importHome);
const Chats = React.lazy(importChats);
const Chat = React.lazy(() => import('./pages/Chat'));
const AinurChat = React.lazy(() => import('./pages/AinurChat'));
const AinurHub = React.lazy(importAIChat);
const AiActivity = React.lazy(() => import('./pages/AiActivity'));
const Homework = React.lazy(() => import('./pages/Homework'));
const ClassLesson = React.lazy(() => import('./pages/ClassLesson'));
const Profile = React.lazy(importProfile);
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const DailyHub = React.lazy(() => import('./pages/DailyHub'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const IntroBooking = React.lazy(() => import('./pages/IntroBooking'));
const PlacementTest = React.lazy(() => import('./pages/PlacementTest'));
const Packages = React.lazy(() => import('./pages/Packages'));
const Admin = React.lazy(() => import('./pages/Admin'));
const Ranking = React.lazy(importRanking);
const Plan = React.lazy(importPlan);
const History = React.lazy(() => import('./pages/History'));
const Progress = React.lazy(() => import('./pages/Progress'));
const Lessons = React.lazy(() => import('./pages/Lessons'));
const Teach = React.lazy(() => import('./pages/Teach'));
const Lesson = React.lazy(() => import('./pages/Lesson'));
const DailyPuzzle = React.lazy(() => import('./pages/DailyPuzzle'));
const Redeem = React.lazy(() => import('./pages/Redeem'));
const JoinTeacher = React.lazy(() => import('./pages/JoinTeacher'));
const TeacherUnlock = React.lazy(() => import('./pages/TeacherUnlock'));
const TeacherStudent = React.lazy(() => import('./pages/TeacherStudent'));
const VideoLibrary = React.lazy(() => import('./pages/VideoLibrary'));

// Shown INSIDE the layout while a page chunk loads — the bottom nav stays
// mounted, so a tab switch never blanks the whole screen.
// Vəziyyət keçidlərini (trial→kurs, kurs→bitmə, premium) reload OLMADAN
// bütün app-a çatdıran sahələr. Yalnız bunlar dəyişəndə user state yenilənir —
// presence heartbeat-in hər 60 saniyəlik lastSeen yazısı re-render yaratmır.
// teacherEligible/completedSessions burada OLMALIDIR: 3-cü sessiya bitən kimi
// müəllim kilidi reload olmadan açılmalıdır — funnel-in bütün gücü məhz o
// andadır. Bu sahələr gündə bir neçə dəfə dəyişir, presence-in 60 saniyəlik
// lastSeen yazısı kimi re-render seli yaratmır.
// The admin suspended this account: sign out and let Login say why.
const SUSPENDED_FLAG = 'slk_suspended'; // read by pages/Login.jsx
function leaveSuspended() {
  try { sessionStorage.setItem(SUSPENDED_FLAG, '1'); } catch { /* private mode */ }
  signOut(auth).catch(() => {});
}

const LIVE_USER_FIELDS = [
  'mode', 'startTick', 'cohortId', 'cohortStatus', 'isPremium', 'subscriptionPlan',
  'premiumPlan', 'trialStartedAt', 'courseActivatedAt', 'courseCompletedAt',
  'freeAccessUntil', 'surveyDone',
  // The onboarding gate reads this; without it the wizard's final write would
  // only lift the gate after a reload.
  'onboardingVersion',
  // Set by the admin after the intro call; unlocks live practice without a reload.
  'introDoneAt',
  // Set when a newcomer taps "Write to us on WhatsApp"; lifts the /intro redirect.
  'introWhatsAppAt',
  'role', 'teacherId', 'teacherEligible', 'completedSessions',
  // Admin təsdiq edən kimi Tutor nişanı reload olmadan görünsün.
  'teacherVerified',
  'preferredLanguage',
];

const fieldValue = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : v);

const PageFallback = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '60vh', color: 'var(--text-muted)', fontSize: 22,
  }} aria-label="Loading">
    <span className="loading-logo"><Mic size={30} strokeWidth={1.75} aria-hidden="true" /></span>
  </div>
);

// One shared shell for both routers. Suspense sits INSIDE AppLayout around
// only the Routes: while a lazy chunk downloads, the nav and layout stay put
// (the old placement unmounted the entire shell into a full-screen loader on
// every first visit to a tab — the "tab switch flash").
// No app-wide trial wall any more (2026-10-04): when the free weeks end only
// planned practice needs a package (functions/packages.js); /packages shows it.

function AppShell({ user }) {
  const location = useLocation();
  // Baseline handler for Android's back button: it consumes nothing, so the
  // hook falls through to history and, at the root, minimises instead of
  // letting Android close the app. Screens that need their own behaviour —
  // Chat during a call — register on top of this one.
  useBackButton(() => false);

  // Dəvət linkini AŞAĞIDAKI route-lardan ƏVVƏL tut: hesabı olmayan biri
  // /join?c=KOD açanda route dərhal /register-ə yönləndirir və kod URL-dən
  // itərdi. Effekt bu render-in location-ını görür, ona görə yönləndirmə
  // baş versə belə kod saxlanılır.
  useEffect(() => {
    if (!location.pathname.startsWith('/join')) return;
    const c = readCodeFromLocation(location.search || window.location.search, window.location.hash);
    if (c) setPendingJoinCode(c);
  }, [location.pathname, location.search]);

  // A "practise with me" link (/p/<uid>, utils/invite.js) is held the same
  // way: a signed-out visitor is sent to register first, and the link would
  // be lost with the URL.
  useEffect(() => {
    const peer = readPeerFromPath(location.pathname);
    if (peer) setPendingPeer(peer);
  }, [location.pathname]);

  // Müəllim şagird sorğusuna göndərilmir (aşağıda /onboarding route-u onu
  // Dashboard-a yönləndirir), amma Lobby ona açıq qalır — özü də məşq edə
  // bilər. Bu cüt yoxlama auth yarışını da bağlayır: Register-in setDoc-u
  // App-ın ilk getDoc-undan gec çatanda müəllim əvvəl /onboarding-ə düşür, rol
  // LIVE_USER_FIELDS onSnapshot-u ilə gələn kimi oradan /teacher-ə atılır.
  const isTeacherUser = user?.role === 'teacher' || isAdminUser(user);
  // Register-in yazdığı birdəfəlik açar (bax /register route-undakı şərh).
  let postRegRole = null;
  try { postRegRole = sessionStorage.getItem('slk_postreg_role'); } catch { /* private mode */ }
  // Açar yalnız qeydiyyat→ilk yönləndirmə pəncərəsi üçündür; rol sənəddən
  // gələn kimi ona ehtiyac qalmır və köhnəlmiş dəyər sonrakı sessiyada başqa
  // rolla qeydiyyatı çaşdırmasın deyə silinir. Hook aşağıdakı erkən
  // return-lərdən ƏVVƏL durmalıdır (rules-of-hooks).
  useEffect(() => {
    if (isTeacherUser && postRegRole) {
      try { sessionStorage.removeItem('slk_postreg_role'); } catch { /* boş */ }
    }
  }, [isTeacherUser, postRegRole]);


  // Qeydiyyatdan sonra axını davam etdir: gözləyən kod var, istifadəçi hələ
  // heç bir müəllimə bağlı deyil → dəvət ekranına qaytar. JoinTeacher həm
  // uğurda, həm "İndi yox"da kodu təmizləyir, ona görə dövr yaranmır.
  // MÜƏLLİM İSTİSNADIR: öz linkini sınayan müəllimin localStorage-ində kod
  // qalırdı və hər açılışda (Dashboard daxil) onu register-ə oxşayan /join
  // ekranına atırdı. Müəllim heç vaxt şagird kimi qoşulmur — kodu silirik.
  if (isTeacherUser && getPendingJoinCode()) clearPendingJoinCode();
  const pendingJoin = user && !user.teacherId && !isTeacherUser && !location.pathname.startsWith('/join')
    ? getPendingJoinCode()
    : '';
  // `surveyDone` ŞƏRTİ QƏSDƏN YOXDUR. Əvvəl qoşulma sorğunun bitməsini
  // gözləyirdi: müəllimin linki ilə gələn adam /survey-də qalıb sorğunu
  // tamamlamasa, kod localStorage-də ölü qalırdı və o şəxs müəllimə HEÇ VAXT
  // qoşulmurdu. Müəllimə bağlanmaq sorğudan vacibdir — JoinTeacher-də "İndi
  // yox" çıxışı var, ona görə heç kim burada qıfıllanmır.
  if (pendingJoin) {
    return <Navigate to={`/join?c=${encodeURIComponent(pendingJoin)}`} replace />;
  }

  // Only the home route is gated, exactly like the old survey: a push that
  // deep-links into /chat or a call must never be stopped by a questionnaire.
  // needsOnboarding() is version-based, so learners who signed up on the old
  // one-page survey are walked through the wizard once too.
  const homeElement = user
    ? (needsOnboarding(user) ? <Navigate to="/onboarding" replace />
      // A newcomer writes to the team on WhatsApp first (utils/intro.js).
      : mustWriteWhatsApp(user) ? <Navigate to="/intro" replace />
        // Joined through a friend's practice link: once the wizard and the
        // WhatsApp step are done, carry on to that friend's chat.
        : getPendingPeer() ? <InviteRedirect user={user} uid={getPendingPeer()} />
          : <Home user={user} />)
    : <Navigate to="/register" />;

  return (
    <AppLayout user={user}>
      {user && <GlobalCallListener user={user} />}
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
          {/* Redirect hədəfi rol-agahdır: qeydiyyat zamanı Register sessionStorage-ə
              rolu yazır; auth yarışında bu route-un stale Navigate effekti bizim
              /teacher naviqasiyasından sonra işləsə belə, eyni hədəfə gedir. */}
          <Route path="/register" element={!user ? <Register /> : <Navigate to={(isTeacherUser || postRegRole === 'teacher') ? '/teacher' : '/'} replace />} />
          <Route path="/onboarding" element={user ? (isTeacherUser ? <Navigate to="/teacher" /> : <Onboarding user={user} />) : <Navigate to="/login" />} />
          {/* Old links (and Register on a stale bundle) still point here. */}
          <Route path="/survey" element={<Navigate to="/onboarding" replace />} />
          <Route path="/intro" element={user ? <IntroBooking user={user} /> : <Navigate to="/login" />} />
          <Route path="/placement" element={user ? <PlacementTest user={user} /> : <Navigate to="/login" />} />
          <Route path="/" element={homeElement} />
          <Route path="/chats" element={user ? <Chats user={user} /> : <Navigate to="/login" />} />
          {/* BEFORE the :peerId route, and it has to be. AInur is not a user
              account, so Chat.jsx would load a peer document that does not
              exist and offer to call somebody who is not there. */}
          {/* "Practise with me" link. A learner who still has the wizard or the
              WhatsApp step ahead goes home first; the link waits (see the
              effect above and homeElement). */}
          <Route path="/p/:uid" element={!user ? <Navigate to="/register" replace />
            : (needsOnboarding(user) || mustWriteWhatsApp(user)) ? <Navigate to="/" replace />
              : <InviteRedirect user={user} uid={readPeerFromPath(location.pathname)} />} />
          <Route path="/chat/ainur" element={user ? <AinurChat user={user} /> : <Navigate to="/login" />} />
          <Route path="/chat/:peerId" element={user ? <Chat user={user} /> : <Navigate to="/login" />} />
          <Route path="/ai-chat" element={user ? <AinurHub user={user} /> : <Navigate to="/login" />} />
          {/* A guided AInur session. Full-screen like a call, so it sits
              outside the tab set rather than inside it. */}
          <Route path="/practice" element={user ? <AiActivity user={user} /> : <Navigate to="/login" />} />
          <Route path="/homework/:id" element={user ? <Homework user={user} /> : <Navigate to="/login" />} />
          <Route path="/class/:lessonId" element={user ? <ClassLesson user={user} /> : <Navigate to="/login" />} />
          <Route path="/profile" element={user ? <Profile user={user} /> : <Navigate to="/login" />} />
          <Route path="/user/:uid" element={user ? <UserProfile user={user} /> : <Navigate to="/login" />} />
          <Route path="/daily" element={user ? <DailyHub /> : <Navigate to="/login" />} />
          <Route path="/puzzle" element={user ? <DailyPuzzle user={user} /> : <Navigate to="/login" />} />
          <Route path="/redeem" element={user ? <Redeem user={user} /> : <Navigate to="/login" />} />
          {/* Dəvət linki: hesabı olmayan biri açanda kod saxlanılır və
              qeydiyyatdan sonra axın qaldığı yerdən davam edir. */}
          <Route path="/join" element={user ? <JoinTeacher user={user} /> : <Navigate to="/register" />} />
          {/* Qeydiyyat yarışı: Register navigate('/teacher') çağıranda App-ın
              user state-i hələ boş ola bilir (onAuthStateChanged zənciri
              bitməyib) və köhnə guard /login→/ bounce-u yaradırdı. Firebase-in
              auth.currentUser-i sinxron dolu olur — auth VAR, state gecikirsə
              yönləndirmə yox, loader göstəririk. */}
          <Route path="/teacher" element={user ? <TeacherUnlock user={user} /> : (auth.currentUser ? <PageFallback /> : <Navigate to="/login" />)} />
          {/* Şagird detalı yalnız müəllim üçündür — qeyri-müəllim Lobby-yə. */}
          <Route path="/teacher/student/:studentId" element={user ? (isTeacherUser ? <TeacherStudent user={user} /> : <Navigate to="/" />) : <Navigate to="/login" />} />
          {/* The whole describe-video deck. Teachers (and the admin) only, and
              web only — the page checks canBrowseAllVideos itself, so the deck
              data stays out of the main bundle. */}
          <Route path="/teacher/videos" element={user ? <VideoLibrary user={user} /> : <Navigate to="/login" />} />
          <Route path="/premium" element={<Navigate to="/packages" replace />} />
          <Route path="/upgrade" element={<Navigate to="/packages" replace />} />
          <Route path="/packages" element={user ? <Packages user={user} /> : <Navigate to="/login" />} />
          <Route path="/ranking" element={user ? <Ranking user={user} /> : <Navigate to="/login" />} />
          <Route path="/plan" element={user ? <Plan user={user} /> : <Navigate to="/login" />} />
          {/* The Live tab (random search, slot board) is gone: practice is
              planned now. Old links and pushes land on the plan instead. */}
          <Route path="/live" element={<Navigate to="/plan" replace />} />
          <Route path="/history" element={user ? <History user={user} /> : <Navigate to="/login" />} />
          {/* History = "what happened in that call"; Progress = "am I getting
              better". Separate routes because they answer separate questions. */}
          <Route path="/lab" element={user ? <Progress user={user} /> : <Navigate to="/login" />} />
          {/* The progress room became the Lab tab (2026-10-01); old links land there. */}
          <Route path="/progress" element={<Navigate to="/lab" replace />} />
          {/* The lessons: how to describe, how to keep a call going, Taboo,
              debate. The map and one lesson are separate routes so a lesson can
              be linked to directly — and so Back from a lesson lands on the
              map rather than leaving the section. */}
          {/* Lesson mode: today's topic as a deck, for a teacher sharing a
              screen. Full-screen like a call, so it sits outside the tab set. */}
          <Route path="/teach" element={user ? <Teach /> : <Navigate to="/login" />} />
          <Route path="/lessons" element={user ? <Lessons user={user} /> : <Navigate to="/login" />} />
          <Route path="/lessons/:lessonId" element={user ? <Lesson user={user} /> : <Navigate to="/login" />} />
          <Route path="/admin" element={isAdminUser(user) ? <Admin user={user} /> : <Navigate to="/" />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}


function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Held only to re-render the tree when the global cycle advances (nightly),
  // so the topic updates live; the value itself is read via the shared cache.
  const [, setCycle] = useState(null);

  useEffect(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    const isMobile = window.innerWidth <= 768;
    if (isPWA || isMobile) {
      document.documentElement.style.setProperty('--app-max-width', '100vw');
      document.body.style.maxWidth = '100vw';
      document.body.style.width = '100vw';
    }

    if (Capacitor.isNativePlatform()) {
      // Set status bar overlay to false so webview sits below it if possible
      // OR set to true if we want to use safe area
      StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
      StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
      SafeArea.getSafeAreaInsets().then(({ insets }) => {
        document.documentElement.style.setProperty('--safe-area-top', `${insets.top}px`);
        document.documentElement.style.setProperty('--safe-area-bottom', `${insets.bottom}px`);
      }).catch(() => {});

      SafeArea.addListener('safeAreaChanged', data => {
        document.documentElement.style.setProperty('--safe-area-top', `${data.insets.top}px`);
        document.documentElement.style.setProperty('--safe-area-bottom', `${data.insets.bottom}px`);
      });

      // The safe-area plugin shrinks the webview by the keyboard height, but
      // the field being typed in is not scrolled up with it: on Login the email
      // box sat under the keyboard and you typed blind (Polad, 2026-10-04).
      // Once the keyboard is up (and the view has shrunk), bring the focused
      // field into the middle of what is left.
      const revealFocused = () => {
        const el = document.activeElement;
        if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
        // The chat composer is pinned to the bottom of a fixed page already.
        if (el.dataset.noReveal) return;
        // Leave fields that are already in sight alone (the chat composer).
        const r = el.getBoundingClientRect();
        if (r.top >= 0 && r.bottom <= window.innerHeight - 8) return;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      };
      Keyboard.addListener('keyboardDidShow', () => setTimeout(revealFocused, 60)).catch(() => {});
      // Moving to the next field while the keyboard stays up fires no event.
      document.addEventListener('focusin', () => setTimeout(revealFocused, 350));
    }
  }, []);

  // Warm the bottom-nav tab chunks shortly after first paint so the first
  // visit to each tab renders from cache instead of downloading its bundle.
  useEffect(() => {
    if (loading) return undefined;
    const t = setTimeout(() => {
      TAB_PAGE_IMPORTS.forEach((load) => load().catch(() => {}));
    }, 2000);
    return () => clearTimeout(t);
  }, [loading]);

  // Subscribe once to the server-driven topic cycle (appConfig/cycle). This
  // warms the shared cache used by getTodayIndex()/getTodayContent() app-wide.
  useEffect(() => subscribeToCycle(setCycle), []);

  useEffect(() => {
    let heartbeatInterval = null;
    let visibilityHandler = null;
    let unloadHandler = null;
    let pageShowHandler = null;
    let stopTokenWatch = null;
    let stopLiveUserSync = null;

    const cleanupPresence = () => {
      if (heartbeatInterval) clearInterval(heartbeatInterval);
      if (visibilityHandler) document.removeEventListener('visibilitychange', visibilityHandler);
      if (unloadHandler) window.removeEventListener('pagehide', unloadHandler);
      if (pageShowHandler) window.removeEventListener('pageshow', pageShowHandler);
      if (stopTokenWatch) { stopTokenWatch(); stopTokenWatch = null; }
      if (stopLiveUserSync) { stopLiveUserSync(); stopLiveUserSync = null; }
    };

    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      cleanupPresence();
      if (currentUser) {
        const uid = currentUser.uid;
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);

        // Suspended by the admin (adminAccess suspend): Auth is disabled too,
        // but a signed-in session lives until its token runs out. Leave now,
        // before anything is written; Login explains.
        if (userSnap.exists() && userSnap.data().suspended) {
          leaveSuspended();
          return;
        }

        // appLanguage/timeZone: bunlar SERVERİN push göndərərkən ehtiyac
        // duyduğu iki siqnaldır — hansı dildə yazsın və randevu saatını hansı
        // qurşaqda çap etsin. Onsuz da olan yazıya minilir, ona görə əlavə
        // Firestore yazısı YOXDUR. Cihaz dəyişəndə (yeni telefon, başqa
        // qurşaq) növbəti girişdə özü yenilənir.
        const tz = deviceTimeZone();
        await setDoc(userRef, {
          uid,
          name: userSnap.exists() ? userSnap.data().name : (currentUser.displayName || 'User'),
          photo: userSnap.exists() && userSnap.data().photo ? userSnap.data().photo : (currentUser.photoURL || ''),
          online: true,
          lastSeen: serverTimestamp(),
          appLanguage: resolveAppLanguage(),
          ...(tz ? { timeZone: tz } : {}),
        }, { merge: true });

        // Keep this device's FCM token fresh for the whole session, not just at
        // load: re-mints on foreground return and on a periodic timer. The two
        // paths are mutually exclusive — the Android WebView has no Web Push,
        // so native builds must take their token from the native FCM SDK.
        stopTokenWatch = isNativePush() ? watchNativePush(uid) : watchFcmToken(uid);

        // Streak reset removed from app-load: Chat.jsx already resets streak
        // to 1 when there is a gap (lastCallDate is older than yesterday).
        // Resetting here was zeroing the streak BEFORE the user could call,
        // so consecutive-day users saw streak = 0 or 1 instead of climbing.

        heartbeatInterval = setInterval(async () => {
          try {
            // While the app is on screen the heartbeat also restates «online»,
            // so a stale «offline» repairs itself within a minute. It used to
            // write lastSeen only, and an «offline» left by an exit that never
            // happened (see pagehide below) stuck for the whole visit: lastSeen
            // stayed fresh, but onlineNow skips status «offline», so two
            // learners in the app could not see each other (Polad 2026-10-17).
            // During a call Chat.jsx owns the status («busy»); a hidden tab
            // leaves it alone too, so a second, visible tab is not undone.
            const restate = document.visibilityState === 'visible' && !isInCall();
            await setDoc(doc(db, 'users', uid), {
              online: true,
              lastSeen: serverTimestamp(),
              ...(restate ? { status: 'online' } : {}),
            }, { merge: true });
          } catch (e) {}
          // The hour-by-hour record (a write only when the hour changes), and
          // only while the app is really in use — the timer keeps running in a
          // backgrounded tab.
          if (document.visibilityState === 'visible' || isInCall()) logPresence(uid);
        }, 60000); // presence heartbeat; ONLINE_WINDOW_MS (150s) must stay ≥2.5× this

        const goOffline = async () => {
          try {
            await setDoc(doc(db, 'users', uid), {
              online: false,
              status: 'offline',
              lastSeen: serverTimestamp(),
            }, { merge: true });
          } catch (e) {}
        };

        // A tab closed mid-call must still stop looking busy to everyone else.
        // pagehide only: beforeunload also fires for navigations that never
        // leave the page (a cancelled one, a download, an app link), and its
        // «offline» then outlived the visit. A page kept in the back/forward
        // cache fires pagehide and returns with pageshow, which says «online».
        unloadHandler = goOffline;
        window.addEventListener('pagehide', unloadHandler);
        pageShowHandler = (e) => {
          if (!e.persisted || isInCall()) return;
          setDoc(doc(db, 'users', uid), {
            online: true,
            status: 'online',
            lastSeen: serverTimestamp(),
          }, { merge: true }).catch(() => {});
        };
        window.addEventListener('pageshow', pageShowHandler);

        visibilityHandler = async () => {
          try {
            // Backgrounding the app during a call (very common on mobile) must
            // not clear the busy flag — the call is still running.
            if (isInCall()) {
              await setDoc(doc(db, 'users', uid), {
                online: true,
                lastSeen: serverTimestamp(),
              }, { merge: true });
              return;
            }
            if (document.visibilityState === 'hidden') await goOffline();
            if (document.visibilityState === 'visible') {
              logPresence(uid);
              await setDoc(doc(db, 'users', uid), {
                online: true,
                status: 'online',
                lastSeen: serverTimestamp(),
              }, { merge: true });
            }
          } catch (e) {}
        };
        document.addEventListener('visibilitychange', visibilityHandler);

        // Ensure user is online and available on initial load
        logPresence(uid);
        try {
          await setDoc(doc(db, 'users', uid), {
            online: true,
            status: 'online',
            lastSeen: serverTimestamp(),
          }, { merge: true });
        } catch (e) {}

        const freshUserSnap = await getDoc(userRef);
        const freshUserData = freshUserSnap.exists() ? freshUserSnap.data() : {};

        const appUser = {
          ...currentUser,
          ...freshUserData,
          uid,
          email: currentUser.email || freshUserData.email || '',
          // Read by isAdminUser: a second admin is known by a verified e-mail.
          emailVerified: currentUser.emailVerified === true,
          displayName: currentUser.displayName || freshUserData.name || 'User',
        };

        setUser(appUser);

        // Cihazlar arası dil sinxronu: sənəddəki preferredLanguage lokal
        // seçimi üstələyir (istifadəçi başqa telefonda dili dəyişibsə burada
        // da tətbiq olunsun). localStorage yalnız ilk render-in flash-ını
        // önləyən keş rolundadır.
        const docLang = freshUserData.preferredLanguage;
        if (docLang === 'az' || docLang === 'tr') {
          setFeedbackLanguage(docLang);
          try { localStorage.setItem(LANG_STORAGE_KEY, docLang); } catch { /* private mode */ }
        }

        // Trial→kurs, tamamlanma və premium keçidləri serverdə yazılan kimi
        // ekranda görünsün (redeem-dən sonra reload tələb olunmasın).
        stopLiveUserSync = onSnapshot(userRef, (snap) => {
          if (!snap.exists()) return;
          const d = snap.data();
          if (d.suspended) { leaveSuspended(); return; }
          setUser((prev) => {
            if (!prev || prev.uid !== uid) return prev;
            const changed = LIVE_USER_FIELDS.some(
              (k) => fieldValue(prev[k]) !== fieldValue(d[k])
            );
            if (!changed) return prev;
            const merged = { ...prev };
            LIVE_USER_FIELDS.forEach((k) => { merged[k] = d[k]; });
            return merged;
          });
        }, () => {});
      } else {
        cleanupPresence();
        setUser(null);
      }

      setLoading(false);
    });

    return () => {
      unsub();
      cleanupPresence();
    };
  }, []);

  if (loading) {
    return (
      <>
        <div className="splash-screen">
          <div className="splash-content" style={{ display: 'flex', justifyContent: 'center' }}>
            <Logo width={240} />
          </div>
          <div className="splash-quote">
            <p className="splash-motto">"The limits of my language are the limits of my world."</p>
            <span className="splash-by">- Ludwig Wittgenstein</span>
          </div>
          <p className="splash-credit">Built by Polad</p>
        </div>
      </>
    );
  }
  return (
    <>
      <ErrorBoundary>
        {Capacitor.isNativePlatform() ? (
          <HashRouter>
            <AppShell user={user} />
          </HashRouter>
        ) : (
          <BrowserRouter>
            <AppShell user={user} />
          </BrowserRouter>
        )}
      </ErrorBoundary>
    </>
  );
}

export default App;

// Opens the chat a practice link points at, once. Your own link, or a broken
// one, lands on Partners instead of an empty chat with yourself.
function InviteRedirect({ user, uid }) {
  clearPendingPeer();
  if (!uid || uid === user.uid) return <Navigate to="/chats" replace />;
  return <Navigate to={`/chat/${uid}`} state={{ invited: true }} replace />;
}
